import {
  AbsoluteCenter,
  Box,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useMemo } from 'react';
import * as Yup from 'yup';

import {
  AnimatedLogo,
  CustomDrawerClosePrimaryButton,
  CustomDrawerFooter,
  CustomDrawerHeader,
  CustomFormErrorMessage,
  PrimaryButton,
  StatusBox,
} from '../../../../components/custom';
import { CustomDropdownMenu } from '../../../../components/custom/CustomDropdown';
import { InputGroupWithLoader } from '../../../../components/custom/CustomInputs';
import useGetUIErrorMessage from '../../../../hooks/useGetUIErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  resetBillBeneficiaryCreation,
  saveBillBeneficiaryAsync,
} from '../../../../store/slices/billPaymentBeneficiaries/saveBeneficiarySlice';
import { customerLookupAsync } from '../../../../store/slices/billsPayment/customerLookupSlice';

const PHONE_NUMBER_LABEL = 'Phone Number';

export const getFormLabel = (billCategory: string) => {
  switch (billCategory) {
    case 'airtime':
      return { billerName: 'Network', customerId: PHONE_NUMBER_LABEL };
    case 'data':
      return { billerName: 'Network', customerId: PHONE_NUMBER_LABEL };
    case 'electricity':
      return { billerName: 'Utility Provider', customerId: 'Meter Number' };
    case 'television':
      return {
        billerName: 'Cable TV Provider',
        customerId: 'Decoder Number',
      };
    case 'betting':
      return {
        billerName: 'Betting Platform',
        customerId: 'Wallet ID',
      };
    default:
      return { billerName: 'Network', customerId: PHONE_NUMBER_LABEL };
  }
};

export default function AddBeneficiaryForm() {
  const dispatch = useAppDispatch();
  const { value: networks } = useAppSelector((state) => state.billerCategories);

  const { status, value } = useAppSelector(
    (state) => state.saveBillBeneficiary
  );

  const { status: lookupStatus, value: lookupValue } = useAppSelector(
    (state) => state.customerLookup
  );

  const errorMessage = useGetUIErrorMessage({
    message: value?.message,
    errors: value?.errors,
  });

  const lookUpErrorMessage = useGetUIErrorMessage({
    message: lookupValue?.message,
    errors: lookupValue?.errors,
  });

  const initialValues = {
    billCategory: '',
    alias: '',
    customerId: '',
    billerName: '',
    billerId: '',
    billItemId: '',
  };

  const validationSchema = Yup.object().shape({
    billCategory: Yup.string().required('Bill type is required'),
    billerName: Yup.string().required('Provider is required'),
    customerId: Yup.string()
      .required('Customer Id is required')
      .min(11)
      .max(16),
    alias: Yup.string(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      dispatch(
        saveBillBeneficiaryAsync({
          billCategory: values.billCategory,
          alias: values.alias,
          customerId: values.customerId,
          billerName: values.billerName,
          billerId: values.billerId,
          billItemId: billItemId as string,
        })
      );
    },
  });

  const {
    errors,
    touched,
    values,
    getFieldProps,
    setFieldValue,
    handleSubmit,
  } = formik;

  const getBillersArrayByName = (nameToFind: string) => {
    if (!networks) {
      return [];
    }

    const network = networks.find((network) => network.name === nameToFind);
    setFieldValue('billerId', network?.id);
    return network?.billers || [];
  };

  const getBillOptions = (billCategory: string) => {
    switch (billCategory) {
      case 'airtime':
      case 'data':
        return (
          getBillersArrayByName('Airtime')?.map((option) => ({
            label: option.name as string,
            value: option.name as string,
          })) || []
        );
      case 'electricity':
        return (
          getBillersArrayByName('Electricity')?.map((option) => ({
            label: option.name as string,
            value: option.name as string,
          })) || []
        );
      case 'television':
        return (
          getBillersArrayByName('Television')?.map((option) => ({
            label: option.name as string,
            value: option.name as string,
          })) || []
        );
      case 'betting':
        return [
          {
            label: 'Bet9ja',
            value: 'Bet9ja',
          },
        ];
      default:
        return [];
    }
  };

  const handleValidation = (billCategory: string) => {
    if (values.customerId.length >= 5) {
      if (values.billCategory === 'electricity') {
        dispatch(
          customerLookupAsync({
            customerId: values.customerId,
            billerId: billItemId ?? '',
            billType: billCategory ?? '',
            billerItemId: billItemId ?? '',
          })
        );
      }
      if (values.billCategory === 'television') {
        dispatch(
          customerLookupAsync({
            customerId: values.customerId,
            billerId: billItemId ?? '',
            billerItemId: billItemId ?? '',
          })
        );
      }
    }
  };

  const renderValidationInformation = (billCategory: string) => {
    return (
      <Stack
        gap={4}
        mt={2}
        sx={{
          backgroundColor: '#FAFAFA',
          padding: '16px',
          borderRadius: '8px',
        }}
      >
        <Text
          sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
          }}
        >
          {billCategory === 'electricity' ? 'Meter' : 'Decoder'} Information
        </Text>
        <Stack gap={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Text
              sx={{
                color: 'richBlack.300',
                fontSize: '0.875rem',
              }}
            >
              {billCategory === 'electricity' ? 'Meter' : 'Decoder'} Name
            </Text>
            <Text
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {lookupValue?.data?.name}
            </Text>
          </Box>
          {billCategory === 'electricity' && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Text
                sx={{
                  color: 'rickBlack.300',
                  fontSize: '0.875rem',
                }}
              >
                Meter Address
              </Text>
              <Text
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {lookupValue?.data?.address}
              </Text>
            </Box>
          )}
        </Stack>
      </Stack>
    );

    // return null;
  };

  const billItemId = useMemo(
    () =>
      networks
        ?.find((n) => n.name?.toLowerCase() === values.billCategory)
        ?.billers?.find(
          (bill) =>
            bill.name?.toLowerCase() === values?.billerName?.toLowerCase()
        )?.id ?? '',
    [networks, values.billCategory, values.billerName]
  );

  const billOptions = useMemo(
    () => getBillOptions(values.billCategory),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.billCategory]
  );

  const billCategoryOptions = [
    {
      value: 'airtime',
      label: 'Airtime',
    },
    {
      value: 'data',
      label: 'Data',
    },
    {
      value: 'electricity',
      label: 'Electricity',
    },
    {
      value: 'television',
      label: 'TV',
    },
    {
      value: 'betting',
      label: 'Betting',
    },
  ];

  if (status === 'loading') {
    return (
      <Box minH="330px">
        <AbsoluteCenter>
          <AnimatedLogo />
        </AbsoluteCenter>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <>
        <Box minH="330px" mb="82px">
          <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
            <StatusBox
              variant="success"
              title="Beneficiary Information Saved Successfully."
            >
              {errorMessage}
            </StatusBox>
          </AbsoluteCenter>
        </Box>
        <CustomDrawerFooter>
          <CustomDrawerClosePrimaryButton
            w="full"
            top="initial"
            right="initial"
            onClick={() => {
              dispatch(resetBillBeneficiaryCreation());
            }}
          >
            Dismiss
          </CustomDrawerClosePrimaryButton>
        </CustomDrawerFooter>
      </>
    );
  }

  if (status === 'failed') {
    return (
      <>
        <Box minH="330px" mb="82px">
          <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
            <StatusBox variant="error" title="Failed to save beneficiary.">
              {errorMessage}
            </StatusBox>
          </AbsoluteCenter>
        </Box>
        <CustomDrawerFooter>
          <PrimaryButton
            onClick={() => dispatch(resetBillBeneficiaryCreation())}
            w="full"
          >
            Retry
          </PrimaryButton>
        </CustomDrawerFooter>
      </>
    );
  }

  return (
    <Box>
      <CustomDrawerHeader startsFromBeginning>
        Add Beneficiary
      </CustomDrawerHeader>
      <Box>
        <Box mb={6}>
          <Text color="#000" mb={1}>
            Beneficiary Details
          </Text>
          <Text color="gray.300" fontSize=".875rem" lineHeight="171.5%">
            Please enter beneficiary details below.
          </Text>
        </Box>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack gap={4}>
              <FormControl
                id="billCategory"
                isInvalid={Boolean(touched.billCategory && errors.billCategory)}
              >
                <FormLabel>Bill Type</FormLabel>
                <CustomDropdownMenu
                  value={values.billCategory}
                  options={billCategoryOptions}
                  fieldValue="billCategory"
                  placeholder="Select Bill Type"
                  setFieldValue={setFieldValue}
                  menuWidth="450px"
                />
                <CustomFormErrorMessage>
                  {touched.billCategory && errors.billCategory}
                </CustomFormErrorMessage>
              </FormControl>

              {values?.billCategory && (
                <Stack gap={4}>
                  <FormControl
                    id="billerName"
                    isInvalid={Boolean(touched.billerName && errors.billerName)}
                  >
                    <FormLabel>Biller</FormLabel>
                    <CustomDropdownMenu
                      searchable
                      value={values.billerName}
                      options={billOptions ?? []}
                      fieldValue="billerName"
                      placeholder="Select Biller"
                      setFieldValue={setFieldValue}
                      menuWidth="450px"
                    />
                    <CustomFormErrorMessage>
                      {touched.billerName && errors.billerName}
                    </CustomFormErrorMessage>
                  </FormControl>
                  <FormControl
                    id="customerId"
                    isInvalid={
                      Boolean(touched.customerId && errors.customerId) ||
                      lookupStatus === 'failed'
                    }
                  >
                    <FormLabel>
                      {getFormLabel(values.billCategory)?.customerId}
                    </FormLabel>
                    <InputGroupWithLoader
                      isLoading={lookupStatus === 'loading'}
                    >
                      <Input
                        {...getFieldProps('customerId')}
                        as="input"
                        placeholder={`Enter ${
                          getFormLabel(values.billCategory)?.customerId
                        }`}
                        isInvalid={Boolean(
                          touched.customerId && errors.customerId
                        )}
                        onBlur={() => handleValidation(values?.billCategory)}
                        maxLength={16}
                      />
                    </InputGroupWithLoader>
                    <CustomFormErrorMessage>
                      {(touched.customerId && errors.customerId) ||
                        (lookupStatus === 'failed' && lookUpErrorMessage)}
                    </CustomFormErrorMessage>
                  </FormControl>

                  {lookupStatus === 'success' &&
                    (values?.billCategory === 'television' ||
                      values?.billCategory === 'electricity') &&
                    renderValidationInformation(values?.billCategory)}

                  <FormControl
                    id="alias"
                    isInvalid={Boolean(touched.alias && errors.alias)}
                  >
                    <FormLabel
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text>Beneficiary Alias </Text>{' '}
                      <Text color="richBlack.200">Optional</Text>
                    </FormLabel>
                    <Input
                      {...getFieldProps('alias')}
                      as="input"
                      placeholder="Enter Beneficiary Alias"
                      isInvalid={Boolean(touched.alias && errors.alias)}
                    />
                    <CustomFormErrorMessage>
                      {touched.alias && errors.alias}
                    </CustomFormErrorMessage>
                  </FormControl>
                </Stack>
              )}
              <CustomDrawerFooter>
                <PrimaryButton
                  w="full"
                  type="submit"
                  disabled={
                    lookupStatus === 'failed' || lookupStatus === 'loading'
                  }
                >
                  Continue
                </PrimaryButton>
              </CustomDrawerFooter>
            </Stack>
          </Form>
        </FormikProvider>
      </Box>
    </Box>
  );
}
